import axios from "axios";
import toast from "react-hot-toast";
import { API_URL, API_ROOT, AUTH_TOKEN_KEY } from "../config";

export const getToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(AUTH_TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(AUTH_TOKEN_KEY);

const http = axios.create({
	baseURL: API_URL,
	timeout: 30000,
});

http.interceptors.request.use((config) => {
	const token = getToken();
	if (token) config.headers.authToken = token;
	return config;
});

http.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error?.response?.status === 401) {
			clearToken();
			toast.error("Session expired, please log in again.");
			window.location.href = "/login";
		}
		return Promise.reject(error);
	}
);

// Matches the ordr-flutter Repository pattern: every call resolves to the
// backend JSON body ({ error, msg, status, data }) instead of throwing on 4xx/5xx,
// since the API returns error payloads with 200-family statuses in most cases.
export async function request(method, path, { data, params, headers, multipart, timeout } = {}) {
	try {
		const res = await http.request({
			method,
			url: path,
			data,
			params,
			timeout,
			headers: multipart ? { ...headers, "Content-Type": "multipart/form-data" } : headers,
		});
		return res.data;
	} catch (err) {
		if (err?.response?.data) return err.response.data;
		// No err.response means the request itself failed (timeout, offline, DNS, CORS, etc.) —
		// axios's raw message ("timeout of 30000ms exceeded") isn't something a user can act on.
		const friendlyMsg = err.code === "ECONNABORTED" ? "Request timed out. Please try again." : "Network error. Please check your connection and try again.";
		// Keyed by message so several requests failing together show one toast, not a stack.
		toast.error(friendlyMsg, { id: friendlyMsg });
		return { error: true, msg: friendlyMsg, data: null };
	}
}

export const get = (path, params, headers) => request("GET", path, { params, headers });
export const post = (path, data, headers) => request("POST", path, { data, headers });
export const put = (path, data, headers) => request("PUT", path, { data, headers });
export const del = (path, data, headers) => request("DELETE", path, { data, headers });

// For v2/v3 endpoints that don't share the v1 prefix baked into the axios baseURL —
// an absolute URL makes axios ignore baseURL entirely.
export const getAbs = (versionedPath, params, headers, timeout) => request("GET", `${API_ROOT}${versionedPath}`, { params, headers, timeout });
export const postAbs = (versionedPath, data, headers) => request("POST", `${API_ROOT}${versionedPath}`, { data, headers });
export const putAbs = (versionedPath, data, headers) => request("PUT", `${API_ROOT}${versionedPath}`, { data, headers });

export default http;
